import type { ComponentType, SVGProps } from 'react';
import {
  contracts,
  PRINT_MATERIAL_TARGET_ID,
  type LinkedApplicationSummary,
  type Permission,
  type WorkspaceGroup,
  type WorkspaceRoutes,
} from '@immo/api-contract';
import { nonRouteRules } from '@/lib/permissions/non-route-rules';
import { mayCall, type PermissionSource } from '@/lib/permissions/route-gate';
import { ACTIVITIES_NAV_COUNT_KEY } from '@/lib/activities-own-open-count';
import {
  CalendarIcon,
  ChartIcon,
  CheckSquareIcon,
  CommercialIcon,
  ContactsIcon,
  ExternalLinkIcon,
  FileIcon,
  HomeIcon,
  ImageIcon,
  InboxIcon,
  KeyIcon,
  PrinterIcon,
  MailIcon,
  MembersIcon,
  OrganizationsIcon,
  PropertiesIcon,
  SendIcon,
  ShieldIcon,
  SparkleIcon,
  TableIcon,
} from './icons';
import type { NavOrigin } from './nav-origin';

export type NavRow = {
  // Resolves to the row's destination once bound to the active workspace
  // (`(r) => r.contacts()`). Omitted on `disabled` rows, which have no page yet.
  readonly to?: (routes: WorkspaceRoutes) => string;
  readonly labelKey: string;
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
  // Exact match for the active state; leaf routes keep the parent row active on
  // their detail pages (e.g. `/properties/<id>` keeps Immobilien active).
  readonly end?: boolean;
  // When set, the row only renders for a caller who may call this route, or holds
  // this registered rule (gated on the permission, never a role name — ADR 0029).
  // Rows without it are always shown.
  readonly gate?: PermissionSource;
  // A second, DATA-shaped condition, evaluated against what the sidebar already
  // holds (IMM-751.4). Additive — every existing row omits it and is unaffected.
  //
  // It exists because some rows have two conditions of genuinely different
  // kinds. „May this member do the thing" is a permission and stays one. „Does
  // this workspace have the thing at all" is configuration, and a permission
  // cannot honestly express it: making one mean "this tenant bought a feature"
  // would turn RBAC into a licensing model, which ADR 0029 deliberately does not
  // carry. Without this, a row gated on permission alone would appear — dead —
  // in every customer's settings but the one that has the application.
  readonly visibleWhen?: (context: NavVisibilityContext) => boolean;
  // Item shown in the menu for IA completeness but with no destination yet
  // (the page is not built). Rendered greyed-out and non-interactive.
  readonly disabled?: boolean;
  // The list this row opens hands its entries this origin (IMM-1347): on a detail page opened from it, the row
  // stays highlighted instead of the row whose path the detail page sits under.
  readonly origin?: NavOrigin;
  // Small caption rendered next to the label (e.g. a "coming soon" tag on a
  // routable-but-unfinished surface). Resolves under the same nav namespace.
  readonly badgeKey?: string;
  // Sub-rows revealed by a disclosure control on the parent. The parent keeps its
  // own `to` (it stays a link — the disclosure is a separate control beside it),
  // and children inherit the parent's `gate` rather than restating it.
  // One level only: a child's own `children` is not rendered.
  readonly children?: ReadonlyArray<NavRow>;
  // Key the row reads its counter under in the `nav-counts` context, which the
  // page owning the list publishes into. Absent — or published as `0` — renders
  // no badge at all: an empty list carries no counter, not a zero.
  readonly countKey?: string;
  // A row that opens one VIEW of a page another row also opens (IMM-1152): its
  // `to` carries `?{param}={value}`, and it is active only while the URL shows that
  // view, never on the path alone. `fallback` marks the view a URL WITHOUT the
  // parameter shows, so a sub-page (an Interessent's detail) keeps that row active.
  readonly view?: { readonly param: string; readonly value: string; readonly fallback?: boolean };
};

export type NavSection = {
  readonly labelKey: string;
  // Optional flow caption rendered next to the section label (e.g. the
  // "Lead → Auftrag" subtitle on the Akquise section in the design).
  readonly subtitleKey?: string;
  readonly items: ReadonlyArray<NavRow>;
};

// Grouped sections mirroring the go-live menu design (IMM-311): the funnel runs
// Akquise (Lead → Auftrag) into Vermarktung (Auftrag → Abschluss), with the
// shared records under Stammdaten. Rows whose page is not built yet are
// `disabled` so the structure is visible without dead links.
export const navSections: ReadonlyArray<NavSection> = [
  // The HLRE area's pages (IMM-1443 to IMM-1445) add their rows above „Mitglieder“.
  {
    labelKey: 'hlre',
    items: [
      {
        to: (r) => r.members(),
        labelKey: 'members',
        icon: MembersIcon,
        gate: contracts.members.list,
        // A workspace that offers Standort roles keeps „Mitglieder“ in its settings menu.
        visibleWhen: (context) => context.offeredRoles.organisationRoles.length === 0,
      },
    ],
  },
  {
    labelKey: 'start',
    items: [
      { to: (r) => r.home(), labelKey: 'dashboard', icon: HomeIcon, end: true, gate: contracts.dashboard.funnel },
      // The Aktivitäten tab (IMM-776.3), replacing „Meine Aufgaben" — same row,
      // new destination and label, now carrying the caller's own open-work count,
      // counted by the shell on every page (IMM-1360.4).
      {
        to: (r) => r.activities(),
        labelKey: 'activities',
        icon: CheckSquareIcon,
        countKey: ACTIVITIES_NAV_COUNT_KEY,
        gate: contracts.activities.feed,
      },
      {
        // Standalone mailbox (IMM-233.3). Gated on the permission the page's own
        // route declares — `mailbox.listMessages` gates on the member-implicit
        // `mailbox-thread-read`, because the Posteingang / Postausgang list the
        // caller's OWN mailbox. It used to gate on `mailbox-connect`, which is
        // workspace-tier and therefore held by Owner/Admin only, so the row was
        // absent for every agent, assistant and viewer (IMM-852, BL-099). The row
        // itself lands on the Posteingang; the disclosure reveals both segments.
        to: (r) => r.mailboxInbox(),
        labelKey: 'emails',
        icon: MailIcon,
        gate: contracts.mailbox.listMessages,
        children: [
          // `end` on the Posteingang: without it the Postausgang (`/mail/sent`, a
          // sub-path of `/mail`) would light both rows at once.
          { to: (r) => r.mailboxInbox(), labelKey: 'emails-inbox', icon: InboxIcon, end: true },
          { to: (r) => r.mailboxOutbox(), labelKey: 'emails-outbox', icon: SendIcon },
        ],
      },
      {
        // Gated on the literal both calendar routes declare — member-implicit
        // `calendar-read`, because the calendar is the caller's OWN (BL-099).
        to: (r) => r.calendar(),
        labelKey: 'calendar',
        icon: CalendarIcon,
        gate: contracts.calendar.listEvents,
      },
      {
        to: (r) => r.chat(),
        labelKey: 'chat',
        icon: SparkleIcon,
        badgeKey: 'chat-experimental',
        gate: contracts.conversations.list,
      },
    ],
  },
  {
    labelKey: 'akquise',
    subtitleKey: 'akquise-flow',
    items: [
      { to: (r) => r.leads(), labelKey: 'owner-leads', icon: ContactsIcon, gate: contracts.leads.list },
      {
        to: (r) => r.deals(),
        labelKey: 'acquisition-pipeline',
        icon: TableIcon,
        gate: contracts.deals.workspaceList,
      },
      // The Standortleitung's cross-deal surface (IMM-430.7). Gated on the
      // oversight capability, so an agent — who reaches the page only as their
      // own narrowed list — is not offered a „Überblick" that shows them what
      // they already see on the pipeline.
      {
        to: (r) => r.dealOverview(),
        labelKey: 'deal-overview',
        icon: TableIcon,
        gate: nonRouteRules.dealOversight,
      },
      // The member's cross-deal Bewertungen (IMM-467.1). Beside Deals rather
      // than beside Aufgaben: the two live in different sections, and the
      // Bewertung is a step of the Akquise flow.
      // Gated on the same grant the route declares
      // (`valuation.contracts.ts` → listOverview): without it the loader's read
      // 403s and the page can only offer a retry that will never succeed.
      {
        to: (r) => r.valuations(),
        labelKey: 'valuations',
        icon: ChartIcon,
        countKey: 'valuations',
        gate: contracts.valuation.listOverview,
      },
    ],
  },
  {
    labelKey: 'vermarktung',
    subtitleKey: 'vermarktung-flow',
    items: [
      {
        to: (r) => r.mandates(),
        labelKey: 'mandates',
        icon: FileIcon,
        gate: contracts.deals.mandates,
        origin: 'mandates',
      },
      // The pipeline's table (IMM-1152): every Interessent in scope, one row each.
      {
        to: (r) => `${r.salesPipeline()}?view=list`,
        labelKey: 'prospects',
        icon: MembersIcon,
        view: { param: 'view', value: 'list' },
        origin: 'prospects',
        gate: contracts.prospects.workspaceList,
      },
      {
        to: (r) => `${r.salesPipeline()}?view=kanban`,
        labelKey: 'sales-pipeline',
        icon: TableIcon,
        view: { param: 'view', value: 'kanban', fallback: true },
        gate: contracts.prospects.workspaceList,
      },
    ],
  },
  {
    labelKey: 'stammdaten',
    items: [
      { to: (r) => r.contacts(), labelKey: 'persons', icon: ContactsIcon, gate: contracts.contacts.workspaceList },
      {
        to: (r) => r.companies(),
        labelKey: 'companies',
        icon: CommercialIcon,
        gate: contracts.companies.workspaceList,
      },
      {
        to: (r) => r.properties(),
        labelKey: 'real-estate',
        icon: PropertiesIcon,
        gate: contracts.properties.workspaceList,
      },
    ],
  },
];

// Settings sub-navigation in the same sectioned shape, so the sidebar's settings
// variant renders it identically. Labels resolve under `pages.settings.nav.*`
// (section keys `personal` / `workspace` live alongside the item keys).
/**
 * Workspace-shaped data the sidebar resolves once and every `visibleWhen`
 * predicate reads. Deliberately narrow: a row's visibility must be decidable
 * from what the shell already has, or the shell grows a fetch per row.
 */
export type NavVisibilityContext = {
  /**
   * The linked applications this workspace is OFFERED (IMM-751.4). Empty for
   * every workspace that has none — which is every customer but one — for a
   * caller who may not read them, since the sidebar does not issue the request
   * at all in that case, and while the answer has not arrived: a row must not
   * flicker into existence.
   *
   * Already filtered by `offeredApplications` (IMM-778.1), so an application
   * whose entry point is hidden never reaches this predicate. The filter is not
   * repeated here on purpose: one filter, one meaning, shared with the page the
   * row leads to.
   *
   * Typed as the CONTRACT's own summary rather than restated structurally, so a
   * field added to the response cannot silently diverge here.
   */
  readonly linkedApplications: ReadonlyArray<LinkedApplicationSummary>;
  /** The roles this workspace offers the caller: what a role-shaped row (Mitglieder, Admins) is decided on. */
  readonly offeredRoles: WorkspaceGroup['offeredRoles'];
};

/** The sections whose rows the caller may open: each row's gate and `visibleWhen`, then the sections left empty dropped. */
export function visibleNavSections(
  sections: ReadonlyArray<NavSection>,
  permissions: ReadonlyArray<Permission>,
  context: NavVisibilityContext,
): ReadonlyArray<NavSection> {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          (item.gate === undefined || mayCall(permissions, item.gate)) &&
          (item.visibleWhen === undefined || item.visibleWhen(context)),
      ),
    }))
    .filter((section) => section.items.length > 0);
}

export const settingsNavSections: ReadonlyArray<NavSection> = [
  {
    labelKey: 'personal',
    items: [
      {
        to: (r) => r.settingsMyProfile(),
        labelKey: 'my-profile',
        icon: MembersIcon,
        gate: contracts.memberProfiles.getMine,
      },
      {
        to: (r) => r.settingsMailbox(),
        labelKey: 'mailbox',
        icon: MailIcon,
        gate: contracts.mailbox.getConnection,
      },
      {
        // Personal, not workspace: a connected MCP client acts as the member who
        // approved it, and every member may connect one. Derived from the list
        // endpoint the page calls, like the API-key row below.
        to: (r) => r.settingsMcp(),
        labelKey: 'mcp',
        icon: ExternalLinkIcon,
        gate: contracts.connectedClients.list,
      },
    ],
  },
  {
    labelKey: 'workspace',
    items: [
      { to: (r) => r.settingsCompany(), labelKey: 'company', icon: OrganizationsIcon, gate: contracts.company.get },
      { to: (r) => r.settingsBranding(), labelKey: 'branding', icon: ImageIcon, gate: contracts.branding.get },
      {
        to: (r) => r.settingsDocumentTemplates(),
        labelKey: 'document-templates',
        icon: FileIcon,
        gate: contracts.documentTemplates.list,
      },
      {
        // Every member sees the state read-only; the manage gate sits on the page's actions.
        to: (r) => r.settingsPortals(),
        labelKey: 'portals',
        icon: ExternalLinkIcon,
        gate: contracts.portalConnections.get,
      },
      {
        to: (r) => r.locations(),
        labelKey: 'locations',
        icon: OrganizationsIcon,
        gate: contracts.organizations.listMine,
      },
      {
        to: (r) => r.members(),
        labelKey: 'members',
        icon: MembersIcon,
        // The members list is for the organisation roles a workspace offers; an HLRE member is offered none.
        visibleWhen: (context) => context.offeredRoles.organisationRoles.length > 0,
      },
      {
        to: (r) => r.settingsAdmins(),
        labelKey: 'admins',
        icon: ShieldIcon,
        gate: contracts.members.changeWorkspaceRole,
        visibleWhen: (context) => context.offeredRoles.workspaceRoles.includes('workspace-admin'),
      },
      {
        to: (r) => r.settingsApiKeys(),
        labelKey: 'api-keys',
        icon: KeyIcon,
        // Derived, not transcribed: the row must vanish for exactly the callers
        // the list route would reject, and a hand-typed literal is how this
        // page's create/revoke gates drifted out of scope in the first place.
        gate: contracts.apiKeys.list,
      },
      {
        to: (r) => r.settingsCompanyMailboxes(),
        labelKey: 'company-mailboxes',
        icon: MailIcon,
        // Derived, not transcribed: the row must vanish for exactly the callers
        // the list route would reject.
        gate: contracts.companyMailboxes.list,
      },
      {
        to: (r) => r.settingsSkills(),
        labelKey: 'skills',
        icon: SparkleIcon,
        // Derived from the endpoint the page calls, not transcribed: the row
        // must vanish for exactly the callers that route would reject.
        gate: contracts.skillInstances.list,
      },
      {
        to: (r) => r.settingsLeadPartners(),
        labelKey: 'lead-partners',
        icon: OrganizationsIcon,
        gate: contracts.leadPartners.list,
      },
      {
        to: (r) => r.settingsPrintMaterial(),
        labelKey: 'print-material',
        icon: PrinterIcon,
        // Derived from the endpoint the page's data comes from, not transcribed:
        // that leaf carries the SAME literal the mint route does, so the row can
        // only appear for a caller minting would accept.
        gate: contracts.appHandoffs.listLinkedApplications,
        // …and the permission is not enough. It is held by ROLE, so without this
        // every Standortleitung in every other customer's workspace would see a
        // dead row (parent Resolved Decision 18). Absent, not disabled: a row
        // reading "not enabled for your workspace" is a sales offer, which is a
        // commercial decision this slice may not make.
        // Named, never "the first one": `LINKED_APPLICATIONS` is a per-workspace
        // ARRAY, so a workspace whose other application happened to be
        // configured would otherwise get a Druckmaterial row — the dead row this
        // mechanism exists to prevent, wearing the right label.
        visibleWhen: (context) =>
          context.linkedApplications.some((application) => application.targetId === PRINT_MATERIAL_TARGET_ID),
      },
    ],
  },
];
